import { itemIdOf } from "../../../api/content/processDefinitions";
import { processTerm } from "../../../api/content/processTerms";
import type { GeneralWorkflow, WorkflowSummary } from "../../../api/content/workflowsApi";

/** The groups of a Workflow list (`workflows.list.groups.*`). */
export type WorkflowGroupName = "favourites" | "recent" | "project" | "basis" | "parts";

/** How many recently used Workflows lead the list. */
export const RECENT_LIMIT = 3;

/** What a group's Workflows share within their origin: the work area they serve (`theme`,
 * processes.md P-20), or what they are more specific kinds of (P-19): a general Workflow, or
 * a Workflow class narrower than `dmwp:Workflow` (a purpose's). */
export type WorkflowKind =
  | { readonly key: string; readonly theme: string }
  | { readonly key: string; readonly general: GeneralWorkflow }
  | { readonly key: string; readonly classIri: string };

/** One group of a Workflow list, in the order shown (F97, F111). */
export interface WorkflowGroup {
  readonly name: WorkflowGroupName;
  /** The kind its Workflows share, after the group of its origin; `null` for that group itself. */
  readonly kind: WorkflowKind | null;
  readonly workflows: readonly WorkflowSummary[];
  /** Folded away until opened: rarely started alone. */
  readonly folded: boolean;
  /** Its rows say where each comes from, as no heading does. */
  readonly marksOrigin: boolean;
}

/**
 * The groups of every Workflow list, the Thread view's chooser and the Workflows sidebar alike
 * (F97, F111, F123): favourites first, then the few used most recently in the project, the
 * project's Workflows, the DMW basis, and last the parts of other Workflows, folded; each
 * Workflow in one group only, empty groups left out. What a Workflow is comes from the listing
 * (`fromBasis`, `partOf`); within a group, those the project runs come first, in the server's
 * order. `recent`: the item IDs of the project's Workflows by their latest start, newest first.
 *
 * What a Workflow is for (processes.md P-20, F123): `project` and `basis` each split into the
 * Workflows of no work area and no kind, then one group per work area its Workflows serve
 * (`theme`), by `themeTitle`, the area's name in the person's language, and last, for
 * Workflows with no theme, one group per kind (P-19), in the order of their first Workflow:
 * a kind the project runs before one it doesn't, as for the Workflows themselves. A
 * Workflow's kind is the general Workflow it is a kind of, else its class where that is
 * narrower than `dmwp:Workflow`. The list titles these groups with the server's words and
 * holds no list of areas or kinds.
 */
export function workflowGroups(workflows: readonly WorkflowSummary[], favourites: ReadonlySet<string>, recent: readonly string[], themeTitle: (theme: string) => string): readonly WorkflowGroup[] {
  const ordered = [...workflows.filter((workflow) => workflow.runsInProject), ...workflows.filter((workflow) => !workflow.runsInProject)];
  const starred = (workflow: WorkflowSummary) => favourites.has(itemIdOf(workflow.reference));
  const unstarred = ordered.filter((workflow) => !starred(workflow));
  const used = recent
    .flatMap((item) => unstarred.find((workflow) => itemIdOf(workflow.reference) === item) ?? [])
    .slice(0, RECENT_LIMIT);
  const rest = unstarred.filter((workflow) => !used.includes(workflow));
  const whole = rest.filter((workflow) => workflow.partOf.length === 0);
  const groups: WorkflowGroup[] = [
    { name: "favourites", kind: null, workflows: ordered.filter(starred), folded: false, marksOrigin: true },
    { name: "recent", kind: null, workflows: used, folded: false, marksOrigin: true },
    ...byKind("project", whole.filter((workflow) => !workflow.fromBasis), themeTitle),
    ...byKind("basis", whole.filter((workflow) => workflow.fromBasis), themeTitle),
    { name: "parts", kind: null, workflows: rest.filter((workflow) => workflow.partOf.length > 0), folded: true, marksOrigin: true },
  ];
  return groups.filter((group) => group.workflows.length > 0);
}

/** Whether a Workflow matches every word searched for, in what the server says it is found by (`searchText`, P-21). */
export function matchesSearch(workflow: WorkflowSummary, words: readonly string[]): boolean {
  const text = workflow.searchText.toLocaleLowerCase();
  return words.every((word) => text.includes(word));
}

/** The words of a search, lower case. */
export function searchWords(query: string): readonly string[] {
  return query.toLocaleLowerCase().split(/\s+/).filter((word) => word !== "");
}

function byKind(name: WorkflowGroupName, workflows: readonly WorkflowSummary[], themeTitle: (theme: string) => string): WorkflowGroup[] {
  const kinds = new Map<string, { kind: WorkflowKind; workflows: WorkflowSummary[] }>();
  const plain: WorkflowSummary[] = [];
  for (const workflow of workflows) {
    const kind = kindOf(workflow);
    if (kind === null) {
      plain.push(workflow);
    } else {
      const group = kinds.get(kind.key) ?? { kind, workflows: [] };
      group.workflows.push(workflow);
      kinds.set(kind.key, group);
    }
  }
  const found = [...kinds.values()];
  const themes = found
    .flatMap((group) => ("theme" in group.kind ? [{ ...group, title: themeTitle(group.kind.theme) }] : []))
    .toSorted((first, second) => first.title.localeCompare(second.title));
  return [
    { name, kind: null, workflows: plain, folded: false, marksOrigin: false },
    ...[...themes, ...found.filter((group) => !("theme" in group.kind))].map((group) => ({ name, kind: group.kind, workflows: group.workflows, folded: false, marksOrigin: false })),
  ];
}

function kindOf(workflow: WorkflowSummary): WorkflowKind | null {
  if (workflow.theme !== null) {
    return { key: `theme:${workflow.theme}`, theme: workflow.theme };
  }
  if (workflow.kindOf !== null) {
    return { key: `item:${itemIdOf(workflow.kindOf.reference)}`, general: workflow.kindOf };
  }
  return workflow.kindClass === processTerm("Workflow") ? null : { key: `class:${workflow.kindClass}`, classIri: workflow.kindClass };
}
